import React, { useCallback, useContext } from 'react';
import { convertFromPopperPlacement } from '../../popper/helpers';
import PositionOnlyPopperPopover from '../../popper/presets/PositionOnlyPopperPopover';
import { ReferenceObject, PopperPlacement, OffsetModifier } from '../../popper/types';
import { setRefs } from '../../../utils/refHelpers';
import { DropdownStateContext } from '../DropdownStateManager';
import { PopperDropdownContext } from './PopperDropdownContext';

export interface Props {
  usePortal?: boolean;
  placement?: PopperPlacement;
  offsetModifier?: OffsetModifier;
  allowFlip?: boolean;
  referenceElement?: ReferenceObject | HTMLElement | null;
  role?: string;
  className?: string;
  popperClassName?: string;
  'aria-label'?: string;
  children?: React.ReactNode;
}

const PopperDropdownContent = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      usePortal,
      placement: popperPlacement = 'bottom-end',
      offsetModifier,
      allowFlip,
      children,
      role,
      className,
      popperClassName,
      referenceElement,
      'aria-label': ariaLabel,
    },
    ref
  ) => {
    const { isOpen, setMenuRef, closeDropdown } = useContext(DropdownStateContext);
    const { toggleElement } = useContext(PopperDropdownContext);
    const { placement, secondaryPlacement } = convertFromPopperPlacement(popperPlacement);
    const onKeyDown = useCallback(
      e => {
        switch (e.key) {
          case 'Escape':
            closeDropdown();
            break;
          default:
            break;
        }
      },
      [closeDropdown]
    );

    return (
      <PositionOnlyPopperPopover
        isVisible={isOpen}
        usePortal={usePortal}
        referenceElement={referenceElement ?? toggleElement}
        popoverContent={children}
        placement={placement}
        secondaryPlacement={secondaryPlacement}
        offsetModifier={offsetModifier}
        allowFlip={allowFlip}
        innerRef={node => setRefs([setMenuRef, ref], node)}
        className={className}
        wrapperClassName={popperClassName}
        onKeyDown={onKeyDown}
        role={role}
        aria-label={ariaLabel}
      />
    );
  }
);
PopperDropdownContent.displayName = 'PopperDropdownContent';

export default PopperDropdownContent;
